// -----------------------------------------------------------------------------
// "New direct message" modal — pick a teammate to start a 1-1 conversation.
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import { fullNameFor } from "../../../utils/chatHelpers";
import { IChatUser } from "../../../models/Chat";
import ChatAvatar from "../ChatAvatar";

interface NewDmModalProps {
  open: boolean;
  onClose: () => void;
  users: IChatUser[];
  starting: boolean;
  onSelect: (userId: string) => void;
}

export default function NewDmModal({
  open,
  onClose,
  users,
  starting,
  onSelect,
}: NewDmModalProps) {
  const { t } = useTranslation("chat");
  const [term, setTerm] = useState("");
  useEffect(() => {
    if (!open) setTerm("");
  }, [open]);

  const filtered = users.filter((u) => {
    const hay = `${u.forename} ${u.surname} ${u.role ?? ""}`.toLowerCase();
    return hay.includes(term.trim().toLowerCase());
  });

  return (
    <Modal open={open} setOpen={(v) => !v && onClose()} width="sm:max-w-md">
      <div className="flex items-center justify-between pb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {t("New direct message")}
          </h3>
          <p className="text-xs text-gray-500">
            {t("Start a 1-1 conversation with a teammate.")}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <label className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("Search teammates")}
          autoFocus
          className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
        />
      </label>

      <div className="mt-3 max-h-72 overflow-y-auto rounded-md border border-gray-200">
        {filtered.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-gray-500">
            <UserGroupIcon className="h-4 w-4 text-gray-400" />
            {t("No teammates match.")}
          </div>
        ) : (
          filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              disabled={starting}
              onClick={() => onSelect(u.id)}
              className="flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50 disabled:opacity-60"
            >
              <ChatAvatar user={u} size="sm" showPresence />
              <span className="flex-1 text-sm">
                <span className="block font-medium text-gray-900">
                  {fullNameFor(u)}
                </span>
                {u.role ? (
                  <span className="block text-xs text-gray-500">{u.role}</span>
                ) : null}
              </span>
            </button>
          ))
        )}
      </div>
    </Modal>
  );
}
